import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import CompanyAddress from "../../components/CompanyAddress";
import SignatureManager from "../../components/SignatureManager";
import ReviewManager from "../../components/ReviewManager";
import logo from "../../images/logo.png";

const API_BASE = process.env.REACT_APP_API_BASE_URL;
const TASK_KEY = "driver_pay_rate_agreement";

const createInitialForm = () => ({
  employeeName: "",
  employeeId: "",
  homeTerminal: "",
  applies: {
    single: false,
    team: false,
    lcv: false,
    cpBci: false,
    abOnly: false,
    canadaOnly: false,
    canadaUs: false,
    abBc: false,
  },
  baseRate: {
    singlePerMile: "",
    singlePikeRate: "",
    singleHourlyRate: "",
    teamPerMile: "",
    teamPikeRate: "",
    teamHourlyRate: "",
  },
  surcharge: {
    singleBcSurcharge: "",
    singleBcMilesMode: "",
    singleUsSurcharge: "",
    singleUsMilesMode: "",
    singlePikeSurcharge: "",
    singleWhitehorseSurcharge: "",
    singleWhitehorseRule: "",
    singleYellowknifeSurcharge: "",
    singleYellowknifeRule: "",
    singleAdditionalLaneSurcharge: "",
    singleAdditionalLaneFor: "",
    teamBcSurcharge: "",
    teamBcMilesMode: "",
    teamUsSurcharge: "",
    teamUsMilesMode: "",
    teamPikeSurcharge: "",
    teamWhitehorseSurcharge: "",
    teamWhitehorseRule: "",
    teamYellowknifeSurcharge: "",
    teamYellowknifeRule: "",
    teamAdditionalLaneSurcharge: "",
    teamAdditionalLaneFor: "",
  },
  safetyBonus: {
    singlePerMile: "",
    singleHourly: "",
    singlePerformance: "",
    teamPerMile: "",
    teamHourly: "",
    teamPerformance: "",
  },
  incentives: {
    singleWait: "",
    singlePickDrop: "",
    singleLayover: "",
    singleHandBombing: "",
    singleHookUnhook: "",
    singleWinterBonus: "",
    teamWait: "",
    teamPickDrop: "",
    teamLayover: "",
    teamHandBombing: "",
    teamHookUnhook: "",
    teamWinterBonus: "",
    lcvHookUnhook: "",
  },
  commentsRemarks: "",
});

const appliesOptions = [
  ["single", "Single"],
  ["team", "Team"],
  ["lcv", "LCV"],
  ["cpBci", "CP BCI"],
  ["abOnly", "AB Only"],
  ["canadaOnly", "Canada Only"],
  ["canadaUs", "Canada/US"],
  ["abBc", "AB/BC"],
];

const notes = [
  "Waiting time is paid only with dispatch approval/customer appointment and paid after 1st hour.",
  "Delays caused by weather conditions, road closures, traffic incidents, mechanical breakdown (unless a layover) or other unforeseen operational interruptions shall not be considered compensable working time.",
  "Layover pay applies only after 24 hours.",
  "First Pick/Drop included in Base pay.",
];

const deriveHomeTerminalFromOrgUnitName = (value) => {
  const raw = String(value || "").trim();
  if (!raw) return "";

  return raw
    .replace(/\s+terminal$/i, "")
    .replace(/\s+office$/i, "")
    .trim();
};

const SectionField = ({
  label,
  value,
  onChange,
  readOnly = false,
  disabled = false,
}) => (
  <div className="mb-2">
    <label className="form-label mb-1">{label}</label>
    <input
      type="text"
      className="form-control"
      value={value}
      readOnly={readOnly}
      disabled={disabled}
      onChange={onChange}
    />
  </div>
);

const PanelCard = ({ title, children }) => (
  <div className="col-lg-6 mb-3">
    <div className="border rounded h-100 p-3 bg-white">
      <h5 className="mb-3">{title}</h5>
      {children}
    </div>
  </div>
);

const InlineTextField = ({ label, value, onChange, disabled = false }) => (
  <div className="d-flex align-items-center mb-3" style={{ gap: 8 }}>
    <label className="form-label mb-0" style={{ minWidth: 165 }}>
      {label}
    </label>
    <input
      type="text"
      className="form-control"
      value={value}
      disabled={disabled}
      onChange={onChange}
      style={{ maxWidth: 180 }}
    />
  </div>
);

const InlineMilesRadio = ({ name, value, onChange, disabled = false }) => (
  <div className="d-flex align-items-center flex-wrap mb-3" style={{ gap: 16 }}>
    <div className="form-check d-flex align-items-center mb-0">
      <input
        id={`${name}-all`}
        type="radio"
        className="form-check-input"
        name={name}
        value="all_miles"
        checked={value === "all_miles"}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
      />
      <label className="form-check-label ms-2" htmlFor={`${name}-all`}>
        All Miles
      </label>
    </div>
    <div className="form-check d-flex align-items-center mb-0">
      <input
        id={`${name}-bc`}
        type="radio"
        className="form-check-input"
        name={name}
        value="bc_miles"
        checked={value === "bc_miles"}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
      />
      <label className="form-check-label ms-2" htmlFor={`${name}-bc`}>
        BC Miles
      </label>
    </div>
  </div>
);

const SurchargePanel = ({ title, values, disabled, onChange }) => (
  <PanelCard title={title}>
    <div className="row">
      <div className="col-md-6">
        <InlineTextField
          label="BC Surcharge:"
          value={values.bcSurcharge}
          disabled={disabled}
          onChange={(e) => onChange("bcSurcharge", e.target.value)}
        />
      </div>
      <div className="col-md-6">
        <InlineMilesRadio
          name={`${title}-bc-miles`}
          value={values.bcMilesMode}
          disabled={disabled}
          onChange={(nextValue) => onChange("bcMilesMode", nextValue)}
        />
      </div>
    </div>

    <div className="row">
      <div className="col-md-6">
        <InlineTextField
          label="US Surcharge:"
          value={values.usSurcharge}
          disabled={disabled}
          onChange={(e) => onChange("usSurcharge", e.target.value)}
        />
      </div>
      <div className="col-md-6 d-flex align-items-center mb-3">
        <span>US Miles Only</span>
      </div>
    </div>

    <InlineTextField
      label="Pike Surcharge:"
      value={values.pikeSurcharge}
      disabled={disabled}
      onChange={(e) => onChange("pikeSurcharge", e.target.value)}
    />

    <div className="row">
      <div className="col-md-6">
        <InlineTextField
          label="Whitehorse Surcharge:"
          value={values.whitehorseSurcharge}
          disabled={disabled}
          onChange={(e) => onChange("whitehorseSurcharge", e.target.value)}
        />
      </div>
      <div className="col-md-6 d-flex align-items-center mb-3">
        <span>Begins/Ends @ Edmonton AB</span>
      </div>
    </div>

    <div className="row">
      <div className="col-md-6">
        <InlineTextField
          label="Yellowknife Surcharge:"
          value={values.yellowknifeSurcharge}
          disabled={disabled}
          onChange={(e) => onChange("yellowknifeSurcharge", e.target.value)}
        />
      </div>
      <div className="col-md-6 d-flex align-items-center mb-3">
        <span>Begins/Ends @ Edmonton AB</span>
      </div>
    </div>

    <div className="row">
      <div className="col-md-6">
        <InlineTextField
          label="Additional Lane Surcharge:"
          value={values.additionalLaneSurcharge}
          disabled={disabled}
          onChange={(e) => onChange("additionalLaneSurcharge", e.target.value)}
        />
      </div>
      <div className="col-md-6 d-flex align-items-center mb-3" style={{ gap: 8 }}>
        <label className="form-label mb-0">For:</label>
        <input
          type="text"
          className="form-control"
          value={values.additionalLaneFor}
          disabled={disabled}
          onChange={(e) => onChange("additionalLaneFor", e.target.value)}
        />
      </div>
    </div>
  </PanelCard>
);

const DriverPayRateAgreementForm = () => {
  const navigate = useNavigate();
  const { id: paramId, dpraId } = useParams();
  const isNew = !dpraId || dpraId === "new";

  const role = localStorage.getItem("userRole");
  const isAdmin = role === "1";
  const loggedInId = localStorage.getItem("userId");
  const userId = isAdmin && paramId ? paramId : loggedInId;

  const [form, setForm] = useState(createInitialForm);
  const [taskId, setTaskId] = useState(null);
  const [taskDisplayTitle, setTaskDisplayTitle] = useState(
    "Driver Pay Rate Agreement",
  );
  const [currentStatus, setCurrentStatus] = useState("not_completed");
  const [isSaving, setIsSaving] = useState(false);
  const [orgUnits, setOrgUnits] = useState([]);

  const updateForm = (path, value) => {
    setForm((prev) => {
      const next = structuredClone(prev);
      let cursor = next;
      for (let index = 0; index < path.length - 1; index += 1) {
        cursor = cursor[path[index]];
      }
      cursor[path[path.length - 1]] = value;
      return next;
    });
  };

  const fetchTaskMeta = useCallback(async () => {
    try {
      const res = await axios.get(`${API_BASE}/api/tasks`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });

      const task = (res.data || []).find((item) => item.t_key === TASK_KEY);
      if (task) {
        setTaskId(task.t_id);
        setTaskDisplayTitle(
          task.t_title || task.t_display_title || "Driver Pay Rate Agreement",
        );
      }
    } catch (err) {
      console.error("Failed to load task metadata", err);
    }
  }, []);

  const fetchTaskStatus = useCallback(async () => {
    if (!userId || !dpraId || dpraId === "new") return;

    try {
      const res = await axios.get(`${API_BASE}/api/usertasks/status/${userId}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });

      const idKey = taskId != null ? `${taskId}_${dpraId}` : null;
      const nameKey = `${TASK_KEY}_${dpraId}`;
      const status =
        (idKey && res.data.status?.[idKey]) ??
        res.data.status?.[nameKey] ??
        res.data.status?.[String(taskId)] ??
        res.data.status?.[TASK_KEY] ??
        "not_completed";

      setCurrentStatus(status);
    } catch (err) {
      console.error("Failed to fetch driver pay rate agreement status", err);
    }
  }, [dpraId, taskId, userId]);

  useEffect(() => {
    fetchTaskMeta();
  }, [fetchTaskMeta]);

  useEffect(() => {
    const fetchOrgUnits = async () => {
      try {
        const res = await axios.get(`${API_BASE}/api/dropdowns/org-units`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        });
        setOrgUnits(Array.isArray(res.data) ? res.data : []);
      } catch (err) {
        console.error("Failed to fetch org units", err);
      }
    };

    fetchOrgUnits();
  }, []);

  useEffect(() => {
    const fetchUser = async () => {
      try {
        const res = await axios.get(`${API_BASE}/api/users/${userId}`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        });

        const employeeName = `${res.data.first_name || ""} ${res.data.last_name || ""}`.trim();
        const employeeId =
          String(res.data.userid_no || res.data.userid || res.data.id || "");
        const orgUnitId = Number(res.data.org_unit);
        const resolvedOrgUnitName =
          orgUnits.find((item) => Number(item.ou_id) === orgUnitId)?.ou_name || "";
        const homeTerminal =
          deriveHomeTerminalFromOrgUnitName(resolvedOrgUnitName) ||
          deriveHomeTerminalFromOrgUnitName(res.data.home_terminal) ||
          "";

        setForm((prev) => ({
          ...prev,
          employeeName,
          employeeId,
          homeTerminal: prev.homeTerminal || homeTerminal,
        }));
      } catch (err) {
        console.error("Failed to fetch user", err);
      }
    };

    if (userId) {
      fetchUser();
    }
  }, [orgUnits, userId]);

  useEffect(() => {
    if (isNew) return;

    const fetchRecord = async () => {
      try {
        const res = await axios.get(
          `${API_BASE}/api/driver-pay-rate-agreement/detail/${dpraId}`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          },
        );

        setForm((prev) => ({
          ...prev,
          ...res.data.payload,
          employeeName: res.data.payload.employeeName || prev.employeeName,
          employeeId: res.data.payload.employeeId || prev.employeeId,
          homeTerminal: res.data.payload.homeTerminal || prev.homeTerminal,
        }));
      } catch (err) {
        console.error("Failed to fetch driver pay rate agreement", err);
        alert("Failed to load Driver Pay Rate Agreement.");
        navigate(`/driver-pay-rate-agreement/${userId}`);
      }
    };

    fetchRecord();
  }, [dpraId, isNew, navigate, userId]);

  useEffect(() => {
    fetchTaskStatus();
  }, [fetchTaskStatus]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSaving(true);

    try {
      const res = await axios.post(
        `${API_BASE}/api/driver-pay-rate-agreement/save`,
        {
          userId,
          dpra_id: isNew ? null : dpraId,
          payload: form,
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        },
      );

      const savedId = res.data?.dpra_id || dpraId;
      alert("Driver Pay Rate Agreement saved successfully!");

      if (isNew && savedId) {
        navigate(`/driver-pay-rate-agreement/${userId}/detail/${savedId}`);
      } else {
        fetchTaskStatus();
      }
    } catch (err) {
      console.error("Save failed", err);
      alert("Failed to save Driver Pay Rate Agreement.");
    } finally {
      setIsSaving(false);
    }
  };

  const isLocked =
    !isAdmin && (currentStatus === "review" || currentStatus === "completed");

  return (
    <>
      <div className="d-flex justify-content-between align-items-center pt-3">
        <span style={{ fontSize: "18px", fontWeight: "500" }}>
          Driver Pay Rate Agreement
        </span>
        <div className="d-flex gap-2">
          <button
            type="button"
            className="btn btn-outline-primary"
            onClick={() => navigate(`/driver-pay-rate-agreement/${userId}`)}
          >
            View List
          </button>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => navigate(-1)}
          >
            Return
          </button>
        </div>
      </div>

      <div className="form-wrapper my-4">
        <div className="row align-items-center mb-4">
          <div className="col-3 text-start">
            <img src={logo} alt="Company Logo" style={{ width: "150px" }} />
          </div>
          <div className="col-6 text-center">
            <h2 className="m-0">{taskDisplayTitle}</h2>
          </div>
          <CompanyAddress />
        </div>

        <form onSubmit={handleSubmit}>
          <div className="row mb-4">
            <div className="col-md-4">
              <SectionField label="Employee Name" value={form.employeeName} readOnly />
            </div>
            <div className="col-md-4">
              <SectionField label="NWT Employee ID" value={form.employeeId} readOnly />
            </div>
            <div className="col-md-4">
              <SectionField
                label="Home Terminal"
                value={form.homeTerminal}
                disabled={isLocked}
                onChange={(e) => updateForm(["homeTerminal"], e.target.value)}
              />
            </div>
          </div>

          <div className="mb-4">
            <label className="form-label d-block">Select all that applies</label>
            <div className="d-flex flex-wrap gap-3">
              {appliesOptions.map(([key, label]) => (
                <div className="form-check" key={key}>
                  <input
                    id={`applies-${key}`}
                    type="checkbox"
                    className="form-check-input"
                    checked={Boolean(form.applies[key])}
                    disabled={isLocked}
                    onChange={(e) =>
                      updateForm(["applies", key], e.target.checked)
                    }
                  />
                  <label className="form-check-label" htmlFor={`applies-${key}`}>
                    {label}
                  </label>
                </div>
              ))}
            </div>
          </div>

          <div className="row">
            <PanelCard title="Single : Authorized Dispatched Miles">
              <SectionField
                label="Per Mile Rate"
                value={form.baseRate.singlePerMile}
                disabled={isLocked}
                onChange={(e) =>
                  updateForm(["baseRate", "singlePerMile"], e.target.value)
                }
              />
              <SectionField
                label="Pike Rate"
                value={form.baseRate.singlePikeRate}
                disabled={isLocked}
                onChange={(e) =>
                  updateForm(["baseRate", "singlePikeRate"], e.target.value)
                }
              />
              <SectionField
                label="Hourly Rate"
                value={form.baseRate.singleHourlyRate}
                disabled={isLocked}
                onChange={(e) =>
                  updateForm(["baseRate", "singleHourlyRate"], e.target.value)
                }
              />
            </PanelCard>

            <PanelCard title="Team : Authorized Dispatched Miles">
              <SectionField
                label="Per Mile Rate"
                value={form.baseRate.teamPerMile}
                disabled={isLocked}
                onChange={(e) =>
                  updateForm(["baseRate", "teamPerMile"], e.target.value)
                }
              />
              <SectionField
                label="Pike Rate"
                value={form.baseRate.teamPikeRate}
                disabled={isLocked}
                onChange={(e) =>
                  updateForm(["baseRate", "teamPikeRate"], e.target.value)
                }
              />
              <SectionField
                label="Hourly Rate"
                value={form.baseRate.teamHourlyRate}
                disabled={isLocked}
                onChange={(e) =>
                  updateForm(["baseRate", "teamHourlyRate"], e.target.value)
                }
              />
            </PanelCard>

            <SurchargePanel
              title="Single"
              disabled={isLocked}
              values={{
                bcSurcharge: form.surcharge.singleBcSurcharge,
                bcMilesMode: form.surcharge.singleBcMilesMode,
                usSurcharge: form.surcharge.singleUsSurcharge,
                pikeSurcharge: form.surcharge.singlePikeSurcharge,
                whitehorseSurcharge: form.surcharge.singleWhitehorseSurcharge,
                yellowknifeSurcharge: form.surcharge.singleYellowknifeSurcharge,
                additionalLaneSurcharge:
                  form.surcharge.singleAdditionalLaneSurcharge,
                additionalLaneFor: form.surcharge.singleAdditionalLaneFor,
              }}
              onChange={(field, value) => {
                const fieldMap = {
                  bcSurcharge: "singleBcSurcharge",
                  bcMilesMode: "singleBcMilesMode",
                  usSurcharge: "singleUsSurcharge",
                  pikeSurcharge: "singlePikeSurcharge",
                  whitehorseSurcharge: "singleWhitehorseSurcharge",
                  yellowknifeSurcharge: "singleYellowknifeSurcharge",
                  additionalLaneSurcharge: "singleAdditionalLaneSurcharge",
                  additionalLaneFor: "singleAdditionalLaneFor",
                };
                updateForm(["surcharge", fieldMap[field]], value);
              }}
            />

            <SurchargePanel
              title="Team"
              disabled={isLocked}
              values={{
                bcSurcharge: form.surcharge.teamBcSurcharge,
                bcMilesMode: form.surcharge.teamBcMilesMode,
                usSurcharge: form.surcharge.teamUsSurcharge,
                pikeSurcharge: form.surcharge.teamPikeSurcharge,
                whitehorseSurcharge: form.surcharge.teamWhitehorseSurcharge,
                yellowknifeSurcharge: form.surcharge.teamYellowknifeSurcharge,
                additionalLaneSurcharge:
                  form.surcharge.teamAdditionalLaneSurcharge,
                additionalLaneFor: form.surcharge.teamAdditionalLaneFor,
              }}
              onChange={(field, value) => {
                const fieldMap = {
                  bcSurcharge: "teamBcSurcharge",
                  bcMilesMode: "teamBcMilesMode",
                  usSurcharge: "teamUsSurcharge",
                  pikeSurcharge: "teamPikeSurcharge",
                  whitehorseSurcharge: "teamWhitehorseSurcharge",
                  yellowknifeSurcharge: "teamYellowknifeSurcharge",
                  additionalLaneSurcharge: "teamAdditionalLaneSurcharge",
                  additionalLaneFor: "teamAdditionalLaneFor",
                };
                updateForm(["surcharge", fieldMap[field]], value);
              }}
            />

            <PanelCard title="Single Safety Bonus (Paid after a month, every 15th)">
              <SectionField
                label="Per Mile"
                value={form.safetyBonus.singlePerMile}
                disabled={isLocked}
                onChange={(e) =>
                  updateForm(["safetyBonus", "singlePerMile"], e.target.value)
                }
              />
              <SectionField
                label="Hourly"
                value={form.safetyBonus.singleHourly}
                disabled={isLocked}
                onChange={(e) =>
                  updateForm(["safetyBonus", "singleHourly"], e.target.value)
                }
              />
              <SectionField
                label="Performance"
                value={form.safetyBonus.singlePerformance}
                disabled={isLocked}
                onChange={(e) =>
                  updateForm(["safetyBonus", "singlePerformance"], e.target.value)
                }
              />
            </PanelCard>

            <PanelCard title="Team Safety Bonus (Paid after a month, every 15th)">
              <SectionField
                label="Per Mile"
                value={form.safetyBonus.teamPerMile}
                disabled={isLocked}
                onChange={(e) =>
                  updateForm(["safetyBonus", "teamPerMile"], e.target.value)
                }
              />
              <SectionField
                label="Hourly"
                value={form.safetyBonus.teamHourly}
                disabled={isLocked}
                onChange={(e) =>
                  updateForm(["safetyBonus", "teamHourly"], e.target.value)
                }
              />
              <SectionField
                label="Performance"
                value={form.safetyBonus.teamPerformance}
                disabled={isLocked}
                onChange={(e) =>
                  updateForm(["safetyBonus", "teamPerformance"], e.target.value)
                }
              />
            </PanelCard>

            <PanelCard title="Single">
              <SectionField
                label="Wait"
                value={form.incentives.singleWait}
                disabled={isLocked}
                onChange={(e) =>
                  updateForm(["incentives", "singleWait"], e.target.value)
                }
              />
              <SectionField
                label="Pick/Drop"
                value={form.incentives.singlePickDrop}
                disabled={isLocked}
                onChange={(e) =>
                  updateForm(["incentives", "singlePickDrop"], e.target.value)
                }
              />
              <SectionField
                label="Layover"
                value={form.incentives.singleLayover}
                disabled={isLocked}
                onChange={(e) =>
                  updateForm(["incentives", "singleLayover"], e.target.value)
                }
              />
              <SectionField
                label="Hand bombing"
                value={form.incentives.singleHandBombing}
                disabled={isLocked}
                onChange={(e) =>
                  updateForm(["incentives", "singleHandBombing"], e.target.value)
                }
              />
              <SectionField
                label="LCV Hook/Unhook"
                value={form.incentives.singleHookUnhook}
                disabled={isLocked}
                onChange={(e) =>
                  updateForm(["incentives", "singleHookUnhook"], e.target.value)
                }
              />
              <SectionField
                label="Winter Bonus"
                value={form.incentives.singleWinterBonus}
                disabled={isLocked}
                onChange={(e) =>
                  updateForm(["incentives", "singleWinterBonus"], e.target.value)
                }
              />
            </PanelCard>

            <PanelCard title="Team">
              <SectionField
                label="Wait"
                value={form.incentives.teamWait}
                disabled={isLocked}
                onChange={(e) =>
                  updateForm(["incentives", "teamWait"], e.target.value)
                }
              />
              <SectionField
                label="Pick/Drop"
                value={form.incentives.teamPickDrop}
                disabled={isLocked}
                onChange={(e) =>
                  updateForm(["incentives", "teamPickDrop"], e.target.value)
                }
              />
              <SectionField
                label="Layover"
                value={form.incentives.teamLayover}
                disabled={isLocked}
                onChange={(e) =>
                  updateForm(["incentives", "teamLayover"], e.target.value)
                }
              />
              <SectionField
                label="Hand bombing"
                value={form.incentives.teamHandBombing}
                disabled={isLocked}
                onChange={(e) =>
                  updateForm(["incentives", "teamHandBombing"], e.target.value)
                }
              />
              <SectionField
                label="LCV Hook/Unhook"
                value={form.incentives.teamHookUnhook}
                disabled={isLocked}
                onChange={(e) =>
                  updateForm(["incentives", "teamHookUnhook"], e.target.value)
                }
              />
              <SectionField
                label="Winter Bonus"
                value={form.incentives.teamWinterBonus}
                disabled={isLocked}
                onChange={(e) =>
                  updateForm(["incentives", "teamWinterBonus"], e.target.value)
                }
              />
            </PanelCard>

          </div>

          <div className="border rounded p-3 mb-4 bg-light">
            <h5 className="mb-3">Notes</h5>
            <ul className="mb-0">
              {notes.map((note) => (
                <li key={note}>{note}</li>
              ))}
            </ul>
          </div>

          <div className="mb-3">
            <label className="form-label">Comments/Remarks</label>
            <textarea
              rows={4}
              className="form-control"
              value={form.commentsRemarks}
              disabled={isLocked}
              onChange={(e) => updateForm(["commentsRemarks"], e.target.value)}
            />
          </div>

          <div className="d-flex justify-content-end">
            <button
              type="submit"
              className="btn btn-primary mt-3"
              disabled={isLocked || isSaving}
            >
              {isSaving ? "Saving..." : "Save"}
            </button>
          </div>
        </form>

        {!isNew && taskId != null && (
          <>
            <hr className="my-4" />
            <SignatureManager
              taskName={TASK_KEY}
              taskId={taskId}
              referenceId={dpraId}
              userId={userId}
              isAdmin={isAdmin}
            />

            <ReviewManager
              taskName={TASK_KEY}
              taskId={taskId}
              referenceId={dpraId}
              userId={userId}
              isAdmin={isAdmin}
              onReviewed={fetchTaskStatus}
            />
          </>
        )}
      </div>
    </>
  );
};

export default DriverPayRateAgreementForm;
